import type {
    AboutProfileContactMethod,
    AboutProfileCredential,
    AboutProfileFact,
    AboutProfileGalleryImage,
    AboutProfilePlatformLink,
    AboutProfileProps,
    AboutProfileTag,
} from "@modes0/ui-components";
import type {MetaTagsProps} from "@/types";
import {categoryDetails as category1} from "@/content/services/1";
import {categoryDetails as category2} from "@/content/services/2";
import {
    BUSINESS_NAME,
    OG_PATH,
    WEBSITE_URL,
    businessDetails,
    contactDetails,
    socialMediaProfiles,
} from "@/content/business-details/business-content";

export const metaTagsAbout: MetaTagsProps = {
    title: "About " + BUSINESS_NAME + " | Sydney Security Services",
    description: "Ozy Wide Group helps Sydney homes, businesses and sites with CCTV, alarms, intercoms, access control, guards, patrols, fire alarms and security consultation.",
    keywords: "about Ozy Wide Group, Sydney security company, security experts Sydney, CCTV and alarm specialists Sydney, licensed security services Sydney",
    url: WEBSITE_URL + "/about/",
    ogImage: OG_PATH + "page-about.jpg",
    type: "website",
};

type AboutProfileColorConfig = NonNullable<AboutProfileProps["colors"]>;

const getReadableTextColor = (backgroundColor: string | undefined) => {
    if (!backgroundColor) return "#111827";

    const normalized = backgroundColor.trim().replace(/^#/, "");
    const hex =
        normalized.length === 3
            ? normalized.split("").map((char) => char + char).join("")
            : normalized;

    if (!/^[0-9a-f]{6}$/i.test(hex)) return "#ffffff";

    const red = parseInt(hex.slice(0, 2), 16) / 255;
    const green = parseInt(hex.slice(2, 4), 16) / 255;
    const blue = parseInt(hex.slice(4, 6), 16) / 255;
    const linear = [red, green, blue].map((channel) =>
        channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4),
    );
    const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];

    return luminance > 0.45 ? "#111827" : "#ffffff";
};

const createAboutProfileColors = (colors: AboutProfileColorConfig): AboutProfileColorConfig => ({
    ...colors,
    accentContrast: getReadableTextColor(colors.accent),
    actionContrast: getReadableTextColor(colors.action),
});

type AboutService = {
    slug?: string;
    title?: string;
    link?: string;
};

type AboutCategory = {
    categoryType?: string;
    slug?: string;
    name?: string;
    title?: string;
    subCategories?: AboutService[];
};

const serviceCategories = [category1, category2] as AboutCategory[];
const primaryPhone = contactDetails.contactNumberOne || "";
const primaryEmail = contactDetails.contactEmail || "";
const businessAbn = businessDetails.companyData?.abn || "";
const businessLicense = businessDetails.companyData?.licenseNumber || "";
const generalServiceArea = businessDetails.generalServiceArea || "Sydney";
const phoneDigits = primaryPhone.replace(/\D/g, "").replace(/^0/, "");
const phoneHref = primaryPhone && phoneDigits ? "tel:+61" + phoneDigits : undefined;
const emailHref = primaryEmail ? "mailto:" + primaryEmail : undefined;

const getCategoryHref = (category: AboutCategory) =>
    category.categoryType && category.slug ? "/" + category.categoryType + "/" + category.slug : "/services";

const getServiceHref = (category: AboutCategory, service: AboutService) =>
    service.link || (service.slug ? getCategoryHref(category) + "/" + service.slug : getCategoryHref(category));

const serviceTags: AboutProfileTag[] = serviceCategories.flatMap((category) => {
    const categoryLabel = category.name || category.title || "Services";
    const categoryTag = {
        id: "category-" + (category.slug || categoryLabel),
        label: categoryLabel,
        href: getCategoryHref(category),
        ariaLabel: "View " + categoryLabel,
    };

    const serviceItems = (category.subCategories || []).slice(0, 4).map((service) => ({
        id: "service-" + (service.slug || service.title || categoryLabel),
        label: service.title || categoryLabel,
        href: getServiceHref(category, service),
        ariaLabel: "View " + (service.title || categoryLabel),
    }));

    return [categoryTag, ...serviceItems];
});

const platformLinks = [
    socialMediaProfiles.instaHandle
        ? {
            id: "instagram",
            label: "Instagram",
            platform: "@" + socialMediaProfiles.instaHandle,
            href: socialMediaProfiles.instaLink + socialMediaProfiles.instaHandle,
            description: "Recent work from " + BUSINESS_NAME + ".",
            target: "_blank",
            rel: "noreferrer",
            icon: "instagram" as const,
        }
        : null,
    socialMediaProfiles.facebookHandle
        ? {
            id: "facebook",
            label: "Facebook",
            platform: socialMediaProfiles.facebookHandle,
            href: socialMediaProfiles.facebookLink + socialMediaProfiles.facebookHandle,
            description: "Updates from " + BUSINESS_NAME + ".",
            target: "_blank",
            rel: "noreferrer",
            icon: "facebook" as const,
        }
        : null,
    socialMediaProfiles.linkedInHandle
        ? {
            id: "linkedin",
            label: "LinkedIn",
            platform: socialMediaProfiles.linkedInHandle,
            href: socialMediaProfiles.linkedInLink + socialMediaProfiles.linkedInHandle,
            description: "Company updates from " + BUSINESS_NAME + ".",
            target: "_blank",
            rel: "noreferrer",
            icon: "linkedin" as const,
        }
        : null,
    socialMediaProfiles.youTubeHandle
        ? {
            id: "youtube",
            label: "YouTube",
            platform: socialMediaProfiles.youTubeHandle,
            href: socialMediaProfiles.youTubeLink + socialMediaProfiles.youTubeHandle,
            description: "Video updates from " + BUSINESS_NAME + ".",
            target: "_blank",
            rel: "noreferrer",
            icon: "youtube" as const,
        }
        : null,
].filter(Boolean) as AboutProfilePlatformLink[];

const quickFacts = [
    {
        id: "business",
        label: "Business",
        value: BUSINESS_NAME,
        description: BUSINESS_NAME + " serves " + generalServiceArea + " customers.",
        icon: "building" as const,
    },
    primaryPhone
        ? {
            id: "phone",
            label: "Phone",
            value: primaryPhone,
            href: phoneHref,
            ariaLabel: "Call " + BUSINESS_NAME + " on " + primaryPhone,
            icon: "phone" as const,
        }
        : null,
    primaryEmail
        ? {
            id: "email",
            label: "Email",
            value: primaryEmail,
            href: emailHref,
            ariaLabel: "Email " + BUSINESS_NAME,
            icon: "mail" as const,
        }
        : null,
    businessLicense
        ? {
            id: "license",
            label: "Licence",
            value: businessLicense,
            icon: "idCard" as const,
        }
        : null,
    businessAbn
        ? {
            id: "abn",
            label: "ABN",
            value: businessAbn,
            icon: "idCard" as const,
        }
        : null,
].filter(Boolean) as AboutProfileFact[];

const credentialItems = [
    businessLicense
        ? {
            id: "license",
            label: "Licence",
            value: businessLicense,
            description: "Licence details are listed for customer reference.",
            icon: "idCard" as const,
        }
        : null,
    businessAbn
        ? {
            id: "abn",
            label: "ABN",
            value: businessAbn,
            description: "ABN details are listed for customer reference.",
            icon: "idCard" as const,
        }
        : null,
    {
        id: "clear-scope",
        label: "Clear scope",
        description: "Work is scoped around the customer, site, timing and practical handover needs.",
        icon: "messageCircle" as const,
    },
    {
        id: "local-service",
        label: generalServiceArea + " service",
        description: "Local support for customers across " + generalServiceArea + " and surrounding suburbs.",
        icon: "mapPin" as const,
    },
].filter(Boolean) as AboutProfileCredential[];

const contactMethods = [
    primaryPhone
        ? {
            id: "call",
            label: "Call",
            value: primaryPhone,
            href: phoneHref,
            ariaLabel: "Call " + BUSINESS_NAME,
            description: "Talk through the job, timing and next practical step.",
            icon: "phone" as const,
        }
        : null,
    primaryEmail
        ? {
            id: "email",
            label: "Email",
            value: primaryEmail,
            href: emailHref,
            ariaLabel: "Email " + BUSINESS_NAME,
            description: "Send a brief, photos, plans or campaign details.",
            icon: "mail" as const,
        }
        : null,
].filter(Boolean) as AboutProfileContactMethod[];

const galleryImages: AboutProfileGalleryImage[] = [
    {
        id: "about-gallery-team",
        src: "/images/parallax/pllx-2.webp",
        lightboxSrc: "/images/parallax/pllx-2.webp",
        alt: "Ozy Wide Group team standing with service vehicles in Sydney",
        title: "Meet the Ozy Wide Group team.",
        caption: "Meet the Ozy Wide Group team.",
        objectPosition: "25% center",
    },
    {
        id: "about-gallery-1",
        src: "/images/parallax/pllx-1.webp",
        lightboxSrc: "/images/parallax/pllx-1.webp",
        alt: "Security system and site protection services in Sydney",
    },
    {
        id: "about-gallery-2",
        src: "/images/services/1/highlights/c1-hp1.webp",
        lightboxSrc: "/images/services/1/highlights/c1-hp1.webp",
        alt: "Service image for Ozy Wide Group sydney security services",
    },
    {
        id: "about-gallery-3",
        src: "/images/services/2/highlights/c2-hp1.webp",
        lightboxSrc: "/images/services/2/highlights/c2-hp1.webp",
        alt: "Service image for Ozy Wide Group sydney security services",
    },
];

const aboutProfileColors = createAboutProfileColors({
        background: "#edf7fc",
        surface: "#ffffff",
        surfaceSoft: "#edf7fc",
        text: "#15171e",
        textSoft: "#384250",
        textMuted: "#64748b",
        accent: "#28aae1",
        accentDark: "#137ea8",
        accentSoft: "color-mix(in srgb, #28aae1 12%, transparent)",
        accentWash: "color-mix(in srgb, #28aae1 7%, transparent)",
        accentContrast: "#ffffff",
        action: "#15171e",
        actionDark: "#020202",
        actionSoft: "color-mix(in srgb, #15171e 12%, transparent)",
        actionContrast: "#ffffff",
        border: "color-mix(in srgb, #28aae1 18%, transparent)",
        borderStrong: "color-mix(in srgb, #28aae1 28%, transparent)",
        shadow: "0 24px 70px rgba(17, 24, 39, 0.11)",
        softShadow: "0 14px 36px rgba(17, 24, 39, 0.08)",
        mediaFallback: "#dff4fc",
        mapBackground: "#edf7fc",
});

const aboutProfileStyle = {
    "--about-profile-accent-contrast": aboutProfileColors.accentContrast,
    "--about-profile-action-contrast": aboutProfileColors.actionContrast,
} as AboutProfileProps["style"];

export const aboutPageContent: AboutProfileProps = {
    id: "about-" + BUSINESS_NAME.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    className: "ozy-wide-about-profile",
    eyebrow: "Sydney electronic security and guarding",
    title: "About " + BUSINESS_NAME,
    description: "Learn about Ozy Wide Group, a Sydney security provider helping homes, businesses and sites with CCTV, alarms, intercoms, access control, guards, patrols and security consultation.",
    heroImage: {
        id: "about-hero",
        src: "/images/parallax/pllx-2.webp",
        alt: "Ozy Wide Group team standing with service vehicles in Sydney",
        title: "Meet the Ozy Wide Group team.",
        caption: "Meet the Ozy Wide Group team.",
        objectPosition: "25% center",
        loading: "eager",
    },
    heroCaption: "Meet the Ozy Wide Group team.",
    actions: [
        {
            label: "Contact us",
            href: "/contact",
            ariaLabel: "Contact " + BUSINESS_NAME,
            icon: "messageCircle",
        },
        {
            label: "View services",
            href: "/services",
            ariaLabel: "View " + BUSINESS_NAME + " services",
            variant: "secondary",
            icon: "arrowRight",
        },
    ],
    stats: [
        {
            value: "Security",
            label: "Core focus",
            description: "Electronic security, guarding, patrols and risk consultation.",
            icon: "shieldCheck",
        },
        {
            value: "Sydney",
            label: "Service area",
            description: "Security support for Sydney homes, businesses and sites.",
            icon: "mapPin",
        },
        {
            value: "Systems",
            label: "Connected protection",
            description: "CCTV, alarms, intercoms, access control, gates and fire alarms.",
            icon: "camera",
        },
    ],
    quickFacts,
    journey: {
        title: "Security planned around the property risk",
        icon: "calendar",
        paragraphs: [
            "Ozy Wide Group helps Sydney homes, businesses and sites with CCTV, alarms, intercoms, access control, guards, patrols, fire alarms and security consultation.",
            "The work starts by looking at entry points, movement, after-hours risk, daily operations and the level of protection the site actually needs.",
        ],
    },
    coreValues: {
        title: "How Ozy Wide scopes security",
        description: "Security work needs to match the property, the people using it and the risks around it.",
        items: [
            {
                title: "Walk through the weak points",
                text: "Entry points, blind spots, staff flow, delivery areas and after-hours exposure shape the recommendation.",
                icon: "shieldCheck",
            },
            {
                title: "Match systems to use",
                text: "CCTV, alarms, access control and intercoms are discussed around how the property is used day to day.",
                icon: "camera",
            },
            {
                title: "Plan monitoring and response",
                text: "Guards, patrols and ongoing support are considered where a system alone is not enough.",
                icon: "users",
            }
        ],
    },
    preferredJobs: {
        title: "Properties Ozy Wide supports",
        items: [
            {
                title: "Homes and apartment sites",
                text: "CCTV, alarms, intercoms and access control for residential properties and shared spaces.",
                icon: "building",
            },
            {
                title: "Businesses and warehouses",
                text: "Security systems, guards and patrols for sites with staff, customers, stock or deliveries.",
                icon: "briefcase",
            },
            {
                title: "Construction and open sites",
                text: "Temporary or ongoing protection where access, theft risk and after-hours movement need attention.",
                icon: "hammer",
            }
        ],
    },
    specialties: {
        title: "Security work customers ask for",
        items: [
            {
                title: "CCTV and alarms",
                text: "Camera coverage, alarm systems and connected security for everyday monitoring.",
                icon: "camera",
            },
            {
                title: "Access and intercoms",
                text: "Entry control for doors, gates, apartments, staff areas and visitor management.",
                icon: "idCard",
            },
            {
                title: "Guards and patrols",
                text: "On-site or mobile support for properties that need visible presence or scheduled checks.",
                icon: "shieldCheck",
            }
        ],
    },
    difference: {
        title: "How security recommendations are made",
        items: [
            {
                title: "The site comes first",
                text: "Recommendations start with access, blind spots, daily movement and what needs protection.",
                icon: "mapPin",
            },
            {
                title: "Systems are explained clearly",
                text: "Customers can compare CCTV, alarms, intercoms, access control, patrols and guards without guesswork.",
                icon: "messageCircle",
            },
            {
                title: "Coverage stays practical",
                text: "The final setup is focused on usable protection, not unnecessary hardware.",
                icon: "badgeCheck",
            }
        ],
    },
    loveOurJob: {
        title: "Security support matched to real use",
        icon: "heartHandshake",
        paragraphs: [
            "Ozy Wide Group looks at how people enter, move through and leave a property before recommending security work.",
            "That keeps the focus on practical coverage across systems, patrols, guards and consultation instead of one-size-fits-all packages.",
        ],
    },
    credentials: {
        title: "Contact and business details",
        description: "Contact details, service area and public business information for " + BUSINESS_NAME + ".",
        items: credentialItems,
    },
    serviceAreas: {
        title: "Service links",
        description: "Services and project types customers usually ask " + BUSINESS_NAME + " about.",
        items: serviceTags,
    },
    platformLinks: platformLinks.length
        ? {
            title: "Social links",
            description: "See more from " + BUSINESS_NAME + " online.",
            items: platformLinks,
        }
        : false,
    gallery: {
        title: "A look at " + BUSINESS_NAME,
        description: "Selected work and service imagery from " + BUSINESS_NAME + ".",
        lightboxLabel: BUSINESS_NAME + " image preview",
        images: galleryImages,
    },
    contact: {
        title: "Talk to " + BUSINESS_NAME,
        description: "Send through the job details, campaign brief or a short note and the team can help with the next practical step.",
        methods: contactMethods,
        action: {
            label: "Open contact page",
            href: "/contact",
            ariaLabel: "Open the " + BUSINESS_NAME + " contact page",
            icon: "arrowRight",
        },
    },
    map: false,
    colors: aboutProfileColors,
    style: aboutProfileStyle,
};

export const aboutPageSearchItem = {
    id: "page-about",
    type: "page" as const,
    label: "About " + BUSINESS_NAME,
    href: "/about",
    description: metaTagsAbout.description,
    imageUrl: "/images/parallax/pllx-2.webp",
    imageAlt: "Ozy Wide Group security team and service vehicles in Sydney",
    category: "Pages",
    keywords: [
        "about " + BUSINESS_NAME,
        BUSINESS_NAME,
        generalServiceArea,
        primaryPhone,
        primaryEmail,
        businessAbn,
        businessLicense,
    ].filter(Boolean),
};
